
import { useState } from 'react';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SalaryReportDialog } from '@/components/admin/SalaryReportDialog';
import { ReportLoading } from '@/components/reports/ReportLoading';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { ReportDetail } from '@/types/salary';
import { useTeacherMonthlySalary } from '@/hooks/useTeacherMonthlySalary';
import { TeacherSalaryHeader } from '@/components/admin/salary/TeacherSalaryHeader';
import { TeacherSalaryTable } from '@/components/admin/salary/TeacherSalaryTable';
import { supabase } from '@/integrations/supabase/client';

const TeacherSalary = () => {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [selectedTeacherReports, setSelectedTeacherReports] = useState<ReportDetail[] | null>(null);

  const {
    teacherMonthlyReports,
    calculateMonthlyReportsMutation,
    calculateSingleTeacherReportMutation,
    refetch
  } = useTeacherMonthlySalary(selectedMonth, selectedYear);

  if (teacherMonthlyReports === undefined) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AdminSidebar />
          <SidebarInset>
            <div className="container mx-auto px-4 py-8">
              <ReportLoading />
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto px-4 py-8">
            <TeacherSalaryHeader
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              onMonthChange={setSelectedMonth}
              onYearChange={setSelectedYear}
              calculateMonthlyReportsMutation={calculateMonthlyReportsMutation}
            />

            <TeacherSalaryTable
              teacherReports={teacherMonthlyReports}
              onViewDetails={async (teacherId: string) => {
                setIsDialogOpen(true);
                setIsLoadingDetails(true);
                setSelectedTeacherReports(null);
                try {
                  const { data, error } = await supabase
                    .from('teacher_monthly_report_details')
                    .select('*')
                    .eq('teacher_id', teacherId)
                    .eq('month', selectedMonth)
                    .eq('year', selectedYear)
                    .order('teaching_date', { ascending: true });
                  if (error) throw error;
                  setSelectedTeacherReports((data ?? []) as unknown as ReportDetail[]);
                } catch (e) {
                  console.error('Failed to fetch report details:', e);
                  setSelectedTeacherReports([]);
                } finally {
                  setIsLoadingDetails(false);
                }
              }}
              calculateSingleTeacherReportMutation={calculateSingleTeacherReportMutation}
            />

            <SalaryReportDialog 
              isOpen={isDialogOpen} 
              onClose={() => setIsDialogOpen(false)} 
              reports={selectedTeacherReports ?? []}
              isLoading={isLoadingDetails}
            />
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default TeacherSalary;
